import { useState } from 'react';
import UserService from '../services/UserService';

const usePDFDownload = () => {
    const [isDownloading, setIsDownloading] = useState(false);

    const downloadPDF = async (bookingFormNumber, options = {}) => {
        if (!bookingFormNumber) {
            console.error('Booking Form Number is required for PDF download');
            alert('Unable to download booking form. Booking Form Number not found.');
            return false;
        }

        setIsDownloading(true);

        try {
            const { onSuccess, onError, filename = `HT-Booking-Form-${bookingFormNumber}.pdf` } = options;

            console.log(`Downloading PDF for Booking Form Number: ${bookingFormNumber}`);

            // Use UserService instead of fetch - this handles auth automatically
            const response = await UserService.downloadBookingFormPDF(bookingFormNumber);
            
            console.log('Response status:', response.status);
            console.log('Response headers:', response.headers);
            console.log('Content size:', response.data.size, 'bytes');

            if (response.status !== 200) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // response.data is already a blob because we set responseType: 'blob'
            const blob = response.data;

            if (blob.size === 0) {
                throw new Error('Empty response received');
            }

            // Check what type of content we actually received
            const contentType = response.headers['content-type'] || '';
            console.log('Content-Type:', contentType);

            // Read a portion of the blob to check if it's HTML or PDF
            const arrayBuffer = await blob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);
            const header = new TextDecoder().decode(uint8Array.slice(0, 100));
            
            console.log('Content header (first 100 chars):', header);

            // Check if it's HTML content
            if (header.includes('<!DOCTYPE html>') || header.includes('<html') || contentType.includes('text/html')) {
                console.log('Received HTML content instead of PDF');
                
                // Convert blob back to text to show user
                const htmlText = await blob.text();
                console.log('HTML content preview:', htmlText.substring(0, 500));
                
                // Save as HTML file instead
                const htmlBlob = new Blob([htmlText], { type: 'text/html' });
                const htmlFilename = filename.replace('.pdf', '.html');
                
                const url = window.URL.createObjectURL(htmlBlob);
                const downloadLink = document.createElement('a');
                downloadLink.href = url;
                downloadLink.download = htmlFilename;
                downloadLink.style.display = 'none';
                
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);
                
                setTimeout(() => {
                    window.URL.revokeObjectURL(url);
                }, 100);

                // Also try to get the direct URL and open it
                try {
                    const urlResponse = await UserService.getBookingFormUrl(bookingFormNumber);
                    if (urlResponse.data.success && urlResponse.data.link) {
                        setTimeout(() => {
                            const newWindow = window.open(urlResponse.data.link, '_blank');
                            if (newWindow) {
                                setTimeout(() => {
                                    alert('To save as PDF:\n1. Press Ctrl+P (Windows) or Cmd+P (Mac)\n2. Choose "Save as PDF" as destination\n3. Click Save');
                                }, 1500);
                            }
                        }, 500);
                    }
                } catch (e) {
                    console.error('Failed to open URL in new window:', e);
                }

                if (onSuccess) {
                    onSuccess();
                }
                return true;
            }

            // Check if it's a valid PDF (PDF files start with %PDF)
            if (!header.startsWith('%PDF')) {
                console.log('Content is not a valid PDF file');
                console.log('File header:', header);
                throw new Error('Received invalid PDF content. The file may be corrupted or in a different format.');
            }

            // It's a valid PDF, proceed with download
            const url = window.URL.createObjectURL(blob);
            const downloadLink = document.createElement('a');
            downloadLink.href = url;
            downloadLink.download = filename;
            downloadLink.style.display = 'none';
            
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 100);

            console.log('PDF download completed successfully');

            if (onSuccess) {
                onSuccess();
            }

            return true;

        } catch (error) {
            console.error('PDF download error:', error);
            
            // Handle axios error response format
            let errorMessage = 'Failed to download booking form. Please try again.';
            
            if (error.response) {
                if (error.response.status === 404) {
                    errorMessage = 'Booking form not found';
                } else if (error.response.status === 401) {
                    errorMessage = 'Authentication required';
                } else if (error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            const { onError } = options;
            
            if (onError) {
                onError(error, errorMessage);
            } else {
                alert(errorMessage);
            }

            return false;
        } finally {
            setIsDownloading(false);
        }
    };

    // Fallback method - opens URL in new window
    const downloadPDFWithInstructions = async (bookingFormNumber, options = {}) => {
        setIsDownloading(true);
        
        try {
            const { onSuccess, onError } = options;
            
            console.log('Using fallback method - opening in new window');
            const response = await UserService.getBookingFormUrl(bookingFormNumber);
            
            if (response.data.success && response.data.link) {
                const newWindow = window.open(response.data.link, '_blank', 'width=1200,height=800');
                if (newWindow) {
                    setTimeout(() => {
                        alert('To download as PDF:\n\n1. Press Ctrl+P (Windows) or Cmd+P (Mac)\n2. In the destination dropdown, choose "Save as PDF"\n3. Click "Save" to download the PDF file\n\nThe PDF will be saved to your Downloads folder.');
                    }, 2000);
                } else {
                    alert('Popup blocked! Please allow popups for this site and try again.');
                    return false;
                }
                
                if (onSuccess) {
                    onSuccess();
                }
                return true;
            } else {
                throw new Error(response.data.message || 'Failed to get booking form URL');
            }
        } catch (error) {
            console.error('Error opening PDF:', error);
            
            const { onError } = options;
            const errorMessage = error.message || 'Failed to open booking form';
            
            if (onError) {
                onError(error, errorMessage);
            } else {
                alert(errorMessage);
            }
            
            return false;
        } finally {
            setIsDownloading(false);
        }
    };

    return {
        downloadPDF,
        downloadPDFWithInstructions,
        isDownloading
    };
};

export default usePDFDownload;